import { sortOut } from './folder-picker';
import type { DroppedPages } from './pending-drop';

/** A file found under a dropped folder and its path from the folder, as `webkitRelativePath` has it. */
interface FoundFile {
  file: File;
  path: string;
}

/**
 * What was dropped, as entries: read synchronously, inside the drop event, because the browser
 * empties the DataTransfer as soon as the handler returns. `null` where the browser has no entries
 * (the caller falls back to the plain file list, which cannot hold a folder).
 */
export function entriesOf(transfer: DataTransfer): FileSystemEntry[] | null {
  const items = [...transfer.items].filter((item) => item.kind === 'file');
  if (items.length === 0 || typeof items[0]!.webkitGetAsEntry !== 'function') return null;
  const entries = items
    .map((item) => item.webkitGetAsEntry())
    .filter((entry): entry is FileSystemEntry => entry !== null);
  return entries.length > 0 ? entries : null;
}

/**
 * The dropped entries as the pickers would have answered them. Any folder among them makes it a
 * folder, read with every subfolder, its files carrying their paths from the dropped folder's
 * parent (`inbox/2026/a.pdf`, the shape `<input webkitdirectory>` gives) and sorted out exactly as
 * the folder picker's are (`sortOut`: readable files in path order, the rest named as skipped).
 * Loose files alone are files, as the file picker would give them.
 */
export async function readDropped(entries: FileSystemEntry[]): Promise<DroppedPages> {
  const found = (await Promise.all(entries.map((entry) => walk(entry, '')))).flat();
  const folders = entries.filter((entry) => entry.isDirectory);
  if (folders.length === 0) return { source: 'files', files: found.map((item) => item.file) };
  const paths = new Map(found.map((item) => [item.file, item.path]));
  const folderName = folders.length === 1 && entries.length === 1 ? folders[0]!.name : '';
  const files = found.map((item) => item.file);
  return {
    source: 'folder',
    picked: sortOut(files, folderName, (file) => paths.get(file) ?? file.name),
  };
}

/** Every file at or under an entry, depth first, with its path joined onto `parent`. */
export async function walk(entry: FileSystemEntry, parent: string): Promise<FoundFile[]> {
  const path = parent ? `${parent}/${entry.name}` : entry.name;
  if (entry.isFile) return [{ file: await fileOf(entry as FileSystemFileEntry), path }];
  if (!entry.isDirectory) return [];
  const children = await readAll((entry as FileSystemDirectoryEntry).createReader());
  const nested = await Promise.all(children.map((child) => walk(child, path)));
  return nested.flat();
}

/**
 * Every entry of a directory. `readEntries` answers in batches (Chrome: at most 100 at a time) and
 * an empty batch is the end, so one call reads only the first hundred files of a large folder.
 */
export async function readAll(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  const all: FileSystemEntry[] = [];
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
      reader.readEntries(resolve, reject),
    );
    if (batch.length === 0) return all;
    all.push(...batch);
  }
}

function fileOf(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}
